import React from 'react';
import { Download, Phone, ArrowLeft, RefreshCw, Eye, Mail } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

interface EdgeActionsProps {
  onUpload?: () => void;
  onAdd?: () => void;
  onFilter?: () => void;
  onCallScript?: () => void;
  onDownloadPDF?: () => void;
  onSendJDEmail?: () => void;
  onBack?: () => void;
  onRefresh?: () => void;
  onViewOriginal?: () => void;
  showUpload?: boolean;
  showAdd?: boolean;
  showFilter?: boolean;
  showCallScript?: boolean;
  showDownloadPDF?: boolean;
  showSendJDEmail?: boolean;
  showBack?: boolean;
  showRefresh?: boolean;
  showViewOriginal?: boolean;
  isDownloadingPDF?: boolean;
}

export const EdgeActions: React.FC<EdgeActionsProps> = ({
  onUpload,
  onAdd,
  onFilter,
  onCallScript,
  onDownloadPDF,
  onSendJDEmail,
  onBack,
  onRefresh,
  onViewOriginal,
  showUpload = true,
  showAdd = true,
  showFilter = true,
  showCallScript = false,
  showDownloadPDF = false,
  showSendJDEmail = false,
  showBack = false,
  showRefresh = false,
  showViewOriginal = false,
  isDownloadingPDF = false
}) => {
  return (
    <div className="fixed right-0 top-1/2 -translate-y-1/2 z-[50] flex flex-col items-end gap-2 group">
      {showBack && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Back</span>
          <button
            onClick={onBack}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
      )}
      {showRefresh && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Refresh</span>
          <button
            onClick={onRefresh}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      )}
      {showViewOriginal && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Original</span>
          <button
            onClick={onViewOriginal}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="View Original Analysis"
            title="View Original Analysis"
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      )}
      {showCallScript && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Call</span>
          <button
            onClick={onCallScript}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Call Script"
            title="Call Script"
          >
            <Phone className="w-5 h-5" />
          </button>
        </div>
      )}
      {showDownloadPDF && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {isDownloadingPDF ? 'Generating...' : 'Download'}
          </span>
          <button
            onClick={onDownloadPDF}
            disabled={isDownloadingPDF}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            aria-label={isDownloadingPDF ? "Generating PDF..." : "Download PDF"}
            title={isDownloadingPDF ? "Generating PDF..." : "Download PDF"}
          >
            {isDownloadingPDF ? (
              <LoadingSpinner size="sm" variant="ai" />
            ) : (
              <Download className="w-5 h-5" />
            )}
          </button>
        </div>
      )}
      {showSendJDEmail && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Send JD</span>
          <button
            onClick={onSendJDEmail}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Send JD Email"
            title="Send JD Email"
          >
            <Mail className="w-5 h-5" />
          </button>
        </div>
      )}
      {showUpload && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Upload</span>
          <button
            onClick={onUpload}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Upload"
            title="Upload"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v9.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0L7.69 11.78a.75.75 0 111.06-1.06l2.47 2.47V3a.75.75 0 01.75-.75z" clipRule="evenodd" />
              <path d="M3.75 15A2.25 2.25 0 016 12.75h12A2.25 2.25 0 0120.25 15v3A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18v-3z" />
            </svg>
          </button>
        </div>
      )}
      {showAdd && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Add</span>
          <button
            onClick={onAdd}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Add"
            title="Add"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M12 4.5a.75.75 0 01.75.75V11h5.75a.75.75 0 010 1.5H12.75v5.75a.75.75 0 01-1.5 0V12.5H5.5a.75.75 0 010-1.5h5.75V5.25A.75.75 0 0112 4.5z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      {showFilter && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-text-secondary dark:text-text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">Filter</span>
          <button
            onClick={onFilter}
            className="bg-white/90 dark:bg-surface-dark-100/90 backdrop-blur-xl border border-border-100 dark:border-border-dark-100 rounded-l-lg shadow-lg p-3 text-text-secondary dark:text-text-secondary hover:bg-white dark:hover:bg-surface-dark-100 hover:scale-105 transition-all duration-200"
            aria-label="Filter"
            title="Filters"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M3 5.25A.75.75 0 013.75 4.5h16.5a.75.75 0 01.53 1.28l-6.03 6.03v5.44a.75.75 0 01-1.1.67l-3-1.5a.75.75 0 01-.4-.67v-3.94L3.22 5.78A.75.75 0 013 5.25z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default EdgeActions;


